import { useEffect, useRef, useState, type FormEvent } from "react";
import { formatStoredPath, normalizeRelativePath } from "../domain/paths";
import type { ScannedBoard, ScannedFolder } from "../workspace/workspace_scan";
import { Modal } from "./modal";

export type BoardManagementRequest =
  | { readonly type: "rename" | "duplicate"; readonly name: string }
  | { readonly type: "move"; readonly folderPath: readonly string[] }
  | { readonly type: "trash" };

export type BoardManagementMode = BoardManagementRequest["type"];

export interface BoardManagementDialogProps {
  readonly board: ScannedBoard;
  readonly folders: readonly ScannedFolder[];
  readonly initialMode?: BoardManagementMode | undefined;
  readonly onClose: () => void;
  readonly onSubmit: (request: BoardManagementRequest) => Promise<void>;
}

type ManagementMode = BoardManagementMode | null;

/** Collects one explicit board management operation without mutating disk directly. */
export function BoardManagementDialog({
  board,
  folders,
  initialMode,
  onClose,
  onSubmit,
}: BoardManagementDialogProps) {
  const [mode, setMode] = useState<ManagementMode>(initialMode ?? null);
  const [name, setName] = useState(board.name);
  const [folderPath, setFolderPath] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const folderSelectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (mode === "rename") {
      nameInputRef.current?.select();
    } else if (mode === "duplicate") {
      nameInputRef.current?.focus();
    } else if (mode === "move") {
      folderSelectRef.current?.focus();
    }
  }, [mode]);

  /** Validates the selected operation before handing it to the journal service. */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (mode === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === "rename" || mode === "duplicate") {
        const normalizedName = normalizeRelativePath([name])[0];
        if (normalizedName === undefined) {
          throw new Error("Enter a board name.");
        }
        await onSubmit({ type: mode, name: normalizedName });
      } else if (mode === "move") {
        const destination = folderPath.length === 0 ? [] : folderPath.split("/");
        await onSubmit({ type: "move", folderPath: destination });
      } else {
        await onSubmit({ type: "trash" });
      }
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The board operation failed safely.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Manage ${board.name}`}
      titleId="manage-board-title"
      initialFocusRef={
        initialMode === "rename" || initialMode === "duplicate"
          ? nameInputRef
          : initialMode === "move"
            ? folderSelectRef
            : undefined
      }
      onClose={onClose}
    >
      <form className="management-dialog" onSubmit={(event) => void handleSubmit(event)}>
        {mode === null ? (
          <div className="management-options">
            <button type="button" onClick={() => setMode("rename")}>
              Rename
            </button>
            <button
              type="button"
              onClick={() => {
                setName(`${board.name} copy`);
                setMode("duplicate");
              }}
            >
              Duplicate
            </button>
            <button type="button" onClick={() => setMode("move")}>
              Move
            </button>
            <button type="button" onClick={() => setMode("trash")}>
              Move to Trash
            </button>
          </div>
        ) : (
          <>
            {mode === "rename" || mode === "duplicate" ? (
              <label>
                Board name
                <input
                  ref={nameInputRef}
                  required
                  value={name}
                  onChange={(event) => setName(event.currentTarget.value)}
                />
              </label>
            ) : null}
            {mode === "move" ? (
              <label>
                Destination folder
                <select
                  ref={folderSelectRef}
                  value={folderPath}
                  onChange={(event) => setFolderPath(event.currentTarget.value)}
                >
                  <option value="">Workspace root</option>
                  {folders.map((folder) => (
                    <option
                      key={formatStoredPath(folder.path)}
                      value={formatStoredPath(folder.path)}
                    >
                      {folder.path.join(" / ")}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {mode === "trash" ? (
              <p>
                The board moves into Shotboard Trash and remains recoverable until Trash is emptied.
              </p>
            ) : null}
            {error === null ? null : (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <footer className="modal__actions">
              <button
                type="button"
                className="secondary-action"
                disabled={busy}
                onClick={() => setMode(null)}
              >
                Back
              </button>
              <button type="submit" className="primary-action" disabled={busy}>
                {busy ? "Working…" : managementActionLabel(mode)}
              </button>
            </footer>
          </>
        )}
      </form>
    </Modal>
  );
}

/** Maps the selected operation to one unambiguous confirmation label. */
function managementActionLabel(mode: Exclude<ManagementMode, null>): string {
  if (mode === "trash") {
    return "Move to Trash";
  }
  return `${mode[0]?.toUpperCase() ?? ""}${mode.slice(1)} Board`;
}
